import { Injectable, Injector, afterNextRender, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, take } from 'rxjs/operators';

import type { StationCard } from '../models/overview.models';
import { OverviewApiService } from './overview-api.service';

const STORAGE_KEY_CODES = 'spice.airport_codes';
/** Matches {@link AuthService} `AUTH_KEY` — read directly to avoid DI cycle with AuthService. */
const AUTH_STORAGE_KEY = 'spice.auth';
/** Maps API airport_id (and card id fallback) → IATA / airport code. */
const STORAGE_KEY_ID_TO_CODE = 'spice.airport_id_to_code';
/** Upper-case IATA / location code → canonical `airport_id` for API filters (see {@link getAirportIdForCode}). */
const STORAGE_KEY_CODE_TO_ID = 'spice.airport_code_to_id';
/**
 * Per IATA: lat/lng/radius from GET /api/v1/airport cards (for POST establishment/search).
 * Radius is miles (`search_radius_miles` on the card).
 */
const STORAGE_KEY_ESTABLISHMENT_SEARCH = 'spice.airport_establishment_search_by_code';
/** Upper-case IATA → country label from last synced GET /api/v1/airport cards (`StationCard.country` / `countryName`). */
const STORAGE_KEY_CODE_TO_COUNTRY = 'spice.airport_code_to_country';

export type StoredEstablishmentSearchFields = {
  latitude?: number;
  longitude?: number;
  /** Search radius in miles */
  radius?: number;
};

/**
 * Persists airport data from GET /api/v1/airport for dropdowns and id→code resolution app-wide.
 * - `airportCodes`: sorted unique IATA codes
 * - `airportIdToCode`: map from `airport_id` (or station card `id`) to that airport's code
 * - `airportCodeToId`: inverse lookup for filters — **prefer `StationCard.airport_id`** over card `id`
 *   when several overview rows share the same IATA.
 */
@Injectable({ providedIn: 'root' })
export class AirportCodesStorageService {
  private readonly injector = inject(Injector);

  /** In-flight GET /api/v1/airport when storage was empty (deduped for concurrent callers). */
  private airportsLoad$: Observable<void> | null = null;

  /** Unique sorted airport codes (e.g. ATL, BOS). */
  readonly airportCodes = signal<string[]>(this.readCodesFromStorage());

  constructor() {
    /** Defer until after bootstrap so lazy OverviewApiService does not create a DI cycle. */
    afterNextRender(() => {
      if (this.isAirportStorageEmpty()) {
        this.triggerAirportsLoadIfEmpty();
      }
    });
  }

  /**
   * True while GET /api/v1/airport is in flight (see OverviewApiService).
   * Used for "Loading Airports..." in Select Airport dropdowns.
   */
  private readonly airportListLoadDepth = signal(0);
  readonly airportsListLoading = computed(() => this.airportListLoadDepth() > 0);

  /** airport_id (string) → airport_code / IATA for lookup when list APIs return only ids. */
  readonly airportIdToCode = signal<Record<string, string>>(this.readIdToCodeFromStorage());

  /** Upper-case IATA / location code → `airport_id` for POST establishment / save filters. */
  readonly airportCodeToId = signal<Record<string, string>>(this.readCodeToIdFromStorage());

  /** IATA → lat/lng/radius from last synced GET /api/v1/airport list. */
  private readonly establishmentSearchByCode = signal<Record<string, StoredEstablishmentSearchFields>>(
    this.readEstablishmentSearchFromStorage()
  );

  /** IATA (upper) → country string from station cards (e.g. USA, Canada, or `countryName` when set). */
  private readonly airportCodeToCountry = signal<Record<string, string>>(this.readCodeToCountryFromStorage());

  /** Incremented when GET /api/v1/airport starts; decremented in finalize (supports overlapping calls). */
  beginAirportListLoad(): void {
    this.airportListLoadDepth.update((d) => d + 1);
  }

  endAirportListLoad(): void {
    this.airportListLoadDepth.update((d) => Math.max(0, d - 1));
  }

  /** True when no IATA codes were persisted (localStorage empty or cleared). */
  isAirportStorageEmpty(): boolean {
    return this.airportCodes().length === 0;
  }

  /**
   * When localStorage has no airport list, GET `/api/v1/airport` and persist via
   * {@link replaceFromStationCards}. No-op when codes already exist. Safe to call from any feature.
   */
  ensureAirportsLoaded(): Observable<void> {
    if (!this.isAirportStorageEmpty()) {
      return of(undefined);
    }
    if (!this.airportsLoad$) {
      const overview = this.injector.get(OverviewApiService);
      this.airportsLoad$ = overview.syncAirportCodesFromGet().pipe(
        map(() => void 0),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => {
          this.airportsLoad$ = null;
        })
      );
    }
    return this.airportsLoad$;
  }

  /** Fire-and-forget load when storage is empty and the user is authenticated. */
  private triggerAirportsLoadIfEmpty(): void {
    if (!this.isAirportStorageEmpty() || !this.hasStoredAuthSession()) {
      return;
    }
    this.ensureAirportsLoaded()
      .pipe(take(1))
      .subscribe({ error: () => undefined });
  }

  /** Same rule as AuthService.readStoredAuth — avoids injecting AuthService (circular DI). */
  private hasStoredAuthSession(): boolean {
    try {
      return Boolean(localStorage.getItem(AUTH_STORAGE_KEY));
    } catch {
      return false;
    }
  }

  /** Replace stored lists (localStorage + signals) from GET /api/v1/airport (overview cards). */
  replaceFromStationCards(cards: StationCard[]): void {
    const codes = this.extractCodes(cards);
    const idToCode = this.extractIdToCodeMap(cards);
    const codeToId = this.extractCodeToIdMap(cards);
    const codeToCountryNext = {
      ...this.airportCodeToCountry(),
      ...this.extractCodeToCountryMap(cards)
    };
    this.persistCodes(codes);
    this.persistIdToCode(idToCode);
    this.persistCodeToId(codeToId);
    this.persistCodeToCountry(codeToCountryNext);
    const newSearch = this.extractEstablishmentSearchByCode(cards);
    const prev = this.establishmentSearchByCode();
    this.persistEstablishmentSearch({ ...prev, ...newSearch });
  }

  /** Merge fields for one IATA code (e.g. after GET /api/v1/airport/:id). */
  mergeEstablishmentSearchForCode(airportCode: string, fields: StoredEstablishmentSearchFields): void {
    const code = airportCode.trim().toUpperCase();
    if (!code) return;
    const prev = this.establishmentSearchByCode();
    const merged: StoredEstablishmentSearchFields = { ...prev[code], ...fields };
    this.persistEstablishmentSearch({ ...prev, [code]: merged });
  }

  /** Lookup IATA/code for an airport_id; returns undefined if unknown. */
  getCodeForAirportId(airportId: string): string | undefined {
    this.triggerAirportsLoadIfEmpty();
    const id = airportId.trim();
    if (!id) return undefined;
    const map = this.airportIdToCode();
    return map[id];
  }

  /** Resolve IATA/code → airport_id (inverse of getCodeForAirportId). */
  getAirportIdForCode(code: string): string | undefined {
    this.triggerAirportsLoadIfEmpty();
    const want = code.trim().toUpperCase();
    if (!want) return undefined;
    const direct = this.airportCodeToId()[want];
    if (direct) return direct;
    /** Legacy: id→code map can list several ids per IATA; iteration order is not stable. */
    const map = this.airportIdToCode();
    for (const [id, c] of Object.entries(map)) {
      if (c.trim().toUpperCase() === want) {
        return id;
      }
    }
    return undefined;
  }

  /**
   * Lat/lng/radius for establishment search from persisted airport list data.
   * Undefined if this airport was never synced or has no usable fields.
   */
  getEstablishmentSearchOverrides(airportCode: string): StoredEstablishmentSearchFields | undefined {
    this.triggerAirportsLoadIfEmpty();
    const code = airportCode.trim().toUpperCase();
    if (!code) return undefined;
    const map = this.establishmentSearchByCode();
    const o = map[code];
    if (!o || typeof o !== 'object') return undefined;
    const has =
      (o.latitude != null && Number.isFinite(o.latitude)) ||
      (o.longitude != null && Number.isFinite(o.longitude)) ||
      (o.radius != null && Number.isFinite(o.radius) && o.radius > 0);
    return has ? { ...o } : undefined;
  }

  /**
   * Full geo + radius for POST establishment/search when all three values are present in storage.
   */
  /**
   * Country for an airport IATA from persisted GET /api/v1/airport cards (prefers API `countryCode`, e.g. US).
   * Empty when unknown or before first airport list sync.
   */
  getStoredCountryForAirportCode(airportCode: string): string {
    this.triggerAirportsLoadIfEmpty();
    const code = airportCode.trim().toUpperCase();
    if (!code) return '';
    const v = this.airportCodeToCountry()[code];
    return this.normalizeCountryCodeValue(v);
  }

  getCompleteEstablishmentSearch(airportCode: string):
    | { latitude: number; longitude: number; radius: number }
    | undefined {
    this.triggerAirportsLoadIfEmpty();
    const code = airportCode.trim().toUpperCase();
    if (!code) return undefined;
    const o = this.establishmentSearchByCode()[code];
    if (!o) return undefined;
    const { latitude: lat, longitude: lng, radius: rad } = o;
    if (
      lat != null &&
      Number.isFinite(lat) &&
      lng != null &&
      Number.isFinite(lng) &&
      rad != null &&
      Number.isFinite(rad) &&
      rad > 0
    ) {
      return { latitude: lat, longitude: lng, radius: rad };
    }
    return undefined;
  }

  /** Clear storage (e.g. on logout). */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY_CODES);
      localStorage.removeItem(STORAGE_KEY_ID_TO_CODE);
      localStorage.removeItem(STORAGE_KEY_CODE_TO_ID);
      localStorage.removeItem(STORAGE_KEY_ESTABLISHMENT_SEARCH);
      localStorage.removeItem(STORAGE_KEY_CODE_TO_COUNTRY);
    } catch {
      /* ignore */
    }
    this.airportCodes.set([]);
    this.airportIdToCode.set({});
    this.airportCodeToId.set({});
    this.establishmentSearchByCode.set({});
    this.airportCodeToCountry.set({});
    this.airportListLoadDepth.set(0);
    this.airportsLoad$ = null;
  }

  private extractCodes(cards: StationCard[]): string[] {
    const set = new Set<string>();
    for (const c of cards) {
      const code = (c.locationCode ?? c.iataCode ?? '').trim();
      if (code) {
        set.add(code);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  private extractIdToCodeMap(cards: StationCard[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const c of cards) {
      const id = (c.airport_id ?? c.id ?? '').trim();
      const code = (c.locationCode ?? c.iataCode ?? '').trim();
      if (id && code) {
        out[id] = code;
      }
    }
    return out;
  }

  /**
   * One canonical `airport_id` per IATA for establishment filters.
   * Prefer `StationCard.airport_id`; only use card `id` when no `airport_id` exists for that code yet.
   */
  private extractCodeToIdMap(cards: StationCard[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const c of cards) {
      const code = (c.locationCode ?? c.iataCode ?? '').trim().toUpperCase();
      if (!code) continue;
      const aid = (c.airport_id ?? '').trim();
      if (aid) {
        out[code] = aid;
      }
    }
    for (const c of cards) {
      const code = (c.locationCode ?? c.iataCode ?? '').trim().toUpperCase();
      if (!code || out[code]) continue;
      const sid = (c.id ?? '').trim();
      if (sid) out[code] = sid;
    }
    return out;
  }

  /** Prefer raw API `countryCode`; fall back to card country labels when missing. */
  private extractCodeToCountryMap(cards: StationCard[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const c of cards) {
      const code = (c.locationCode ?? c.iataCode ?? '').trim().toUpperCase();
      if (!code) continue;
      const fromCode = this.normalizeCountryCodeValue(c.countryCode);
      const fromEnum = this.normalizeCountryCodeValue(c.country);
      const fromName = this.normalizeCountryCodeValue(c.countryName);
      const merged = fromCode || fromEnum || fromName;
      if (merged) {
        out[code] = merged;
      }
    }
    return out;
  }

  /**
   * Keep airport country values in code form for downstream payloads (`US`, `CA`).
   * Falls back to upper-case token for unknown values.
   */
  private normalizeCountryCodeValue(raw: unknown): string {
    const t = String(raw ?? '').trim();
    if (!t) return '';
    const u = t.toUpperCase();
    if (u === 'US' || u === 'USA' || u === 'UNITED STATES' || u === 'UNITED STATES OF AMERICA') return 'US';
    if (u === 'CA' || u === 'CAN' || u === 'CANADA') return 'CA';
    return u;
  }

  private persistCodeToCountry(map: Record<string, string>): void {
    try {
      localStorage.setItem(STORAGE_KEY_CODE_TO_COUNTRY, JSON.stringify(map));
    } catch {
      /* ignore */
    }
    this.airportCodeToCountry.set(map);
  }

  private readCodeToCountryFromStorage(): Record<string, string> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CODE_TO_COUNTRY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        const code = k.trim().toUpperCase();
        const s =
          typeof v === 'string' ? v.trim() : typeof v === 'number' && Number.isFinite(v) ? String(v) : '';
        if (code && s) out[code] = s;
      }
      return out;
    } catch {
      return {};
    }
  }

  private persistCodes(codes: string[]): void {
    try {
      localStorage.setItem(STORAGE_KEY_CODES, JSON.stringify(codes));
    } catch {
      /* ignore */
    }
    this.airportCodes.set(codes);
  }

  private persistIdToCode(map: Record<string, string>): void {
    try {
      localStorage.setItem(STORAGE_KEY_ID_TO_CODE, JSON.stringify(map));
    } catch {
      /* ignore */
    }
    this.airportIdToCode.set(map);
  }

  private persistCodeToId(map: Record<string, string>): void {
    try {
      localStorage.setItem(STORAGE_KEY_CODE_TO_ID, JSON.stringify(map));
    } catch {
      /* ignore */
    }
    this.airportCodeToId.set(map);
  }

  private readCodeToIdFromStorage(): Record<string, string> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CODE_TO_ID);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        const code = k.trim().toUpperCase();
        const id = typeof v === 'string' ? v.trim() : '';
        if (code && id) out[code] = id;
      }
      return out;
    } catch {
      return {};
    }
  }

  private readCodesFromStorage(): string[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CODES);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
    } catch {
      return [];
    }
  }

  private readIdToCodeFromStorage(): Record<string, string> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_ID_TO_CODE);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof k === 'string' && typeof v === 'string' && k.trim() && v.trim()) {
          out[k.trim()] = v.trim();
        }
      }
      return out;
    } catch {
      return {};
    }
  }

  private extractEstablishmentSearchByCode(cards: StationCard[]): Record<string, StoredEstablishmentSearchFields> {
    const out: Record<string, StoredEstablishmentSearchFields> = {};
    for (const c of cards) {
      const code = (c.locationCode ?? c.iataCode ?? '').trim().toUpperCase();
      if (!code) continue;
      const entry: StoredEstablishmentSearchFields = {};
      const lat = parseFloat(String(c.latitude ?? '').trim());
      const lng = parseFloat(String(c.longitude ?? '').trim());
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        entry.latitude = lat;
        entry.longitude = lng;
      }
      const rad = parseFloat(String(c.search_radius_miles ?? '').trim());
      if (Number.isFinite(rad) && rad > 0) {
        entry.radius = rad;
      }
      if (Object.keys(entry).length > 0) {
        out[code] = entry;
      }
    }
    return out;
  }

  private persistEstablishmentSearch(map: Record<string, StoredEstablishmentSearchFields>): void {
    try {
      localStorage.setItem(STORAGE_KEY_ESTABLISHMENT_SEARCH, JSON.stringify(map));
    } catch {
      /* ignore */
    }
    this.establishmentSearchByCode.set(map);
  }

  private readEstablishmentSearchFromStorage(): Record<string, StoredEstablishmentSearchFields> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_ESTABLISHMENT_SEARCH);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const out: Record<string, StoredEstablishmentSearchFields> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        const code = k.trim().toUpperCase();
        if (!code || !v || typeof v !== 'object' || Array.isArray(v)) continue;
        const o = v as Record<string, unknown>;
        const entry: StoredEstablishmentSearchFields = {};
        const lat = typeof o['latitude'] === 'number' ? o['latitude'] : parseFloat(String(o['latitude'] ?? ''));
        const lng = typeof o['longitude'] === 'number' ? o['longitude'] : parseFloat(String(o['longitude'] ?? ''));
        const rad = typeof o['radius'] === 'number' ? o['radius'] : parseFloat(String(o['radius'] ?? ''));
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          entry.latitude = lat;
          entry.longitude = lng;
        }
        if (Number.isFinite(rad) && rad > 0) {
          entry.radius = rad;
        }
        if (Object.keys(entry).length > 0) {
          out[code] = entry;
        }
      }
      return out;
    } catch {
      return {};
    }
  }
}
